import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getUserDetail, refreshTokenReq, type UserInfoModel } from '@/api/auth'
import { isToday } from '@/utils'

export type UserInfo = UserInfoModel

export const useUserStore = defineStore('user', () => {
  const token = ref<string>(localStorage.getItem('app-token') || '')
  const lastTokenRefresh = ref<number>(Number(localStorage.getItem('app-last-token-refresh') || 0))

  const storedUserInfo = localStorage.getItem('app-user-info')
  const userInfo = ref<UserInfoModel | null>(
    storedUserInfo ? JSON.parse(storedUserInfo) : null
  )

  const isLoggedIn = computed(() => !!token.value)
  const roles = computed(() => userInfo.value?.roles ?? [])

  function login(newToken: string, info: UserInfoModel): void {
    token.value = newToken
    userInfo.value = info
    lastTokenRefresh.value = Date.now()
    localStorage.setItem('app-token', newToken)
    localStorage.setItem('app-user-info', JSON.stringify(info))
    localStorage.setItem('app-last-token-refresh', Date.now().toString())
  }

  function setUserInfo(info: UserInfoModel): void {
    userInfo.value = info
    localStorage.setItem('app-user-info', JSON.stringify(info))
  }

  function logout(): void {
    token.value = ''
    userInfo.value = null
    lastTokenRefresh.value = 0
    localStorage.removeItem('app-token')
    localStorage.removeItem('app-user-info')
    localStorage.removeItem('app-last-token-refresh')
  }

  function hasRole(role: string): boolean {
    return roles.value.includes(role)
  }

  async function refreshToken() {
    if (!token.value) {
      return
    }
    try {
      if (isToday(lastTokenRefresh.value)) {
        const result = await getUserDetail()
        if (result) {
          setUserInfo(result)
        } else {
          logout()
        }
      } else {
        const result = await refreshTokenReq();
        if (result.token && result.userInfo) {
          login(result.token, result.userInfo)
        }
      }
    } catch (error) {
      logout()
    }
  }

  refreshToken();
  return { token, userInfo, isLoggedIn, roles, login, setUserInfo, logout, hasRole }
})
